// Application models - converted from ActionScript

import { UserInfo, ImageInfo } from './user.model';
import { Category, ProductInfo } from './product.model';

export interface PartnerInfo {
  id: number;
  name: string;
  logo?: ImageInfo;
  showLogo: number;
}

export interface Country {
  id: number;
  name: string;
  /** ISO 3166-1 alpha-2 code (e.g., "PT", "BR"). Backend field is `iso`. */
  code: string;
  states?: State[];
  // Additive fields from the dreambooks-api alignment (GET /orders/countries-with-points
  // and other country-returning endpoints). Optional because legacy responses may
  // omit them and the frontend doesn't consume them yet — wire them up if you need
  // postal-code masking or EU/VIES handling.
  /** ISO code used for VAT VIES validation — may differ from `code` (e.g., Greece: EL vs GR). */
  isoVies?: string;
  /** 1 if part of the EU customs union. */
  euMember?: number;
  /** 1 if shipments are currently allowed to this country. */
  enabled?: number;
  /** Mask pattern for postal codes — `#` = digit, `A` = letter (e.g., "####-###" for PT). */
  postalMask?: string;
}

export interface State {
  id: number;
  name: string;
  code: string;
  countryId: number;
  cities?: City[];
}

export interface City {
  id: number;
  name: string;
  stateId: number;
}

export interface AppModel {
  state: AppState;
  countries: Country[];
  adminUser?: UserInfo;
  locale: string;
  serverPing: boolean;
  partner?: PartnerInfo;
  uploading: boolean;
  categories: Category[];
  searchResults: ProductInfo[];
}

export enum AppState {
  LOADING = 'loading',
  UPDATE = 'update',
  LOGIN = 'login',
  PLAFOND = 'plafond',
  GUEST_PREVIEW = 'guestPreview',  // Guest album preview (no topbar)
  
  // ONELAB STATES
  HOME_ONELABPRO = 'onelabHome',
  GIFTS = 'gifts',
  CALENDARS = 'calendars',
  DECOR = 'decor',
  RIGIDS = 'rigids',
  FLEXIBLES = 'flexibles',
  PHOTOS_POSTERS = 'photosPosters',
  GREETINGS = 'greetings',
  OFFSET = 'offset',
  CARDS = 'cards',
  FRAMES = 'frames',
  FILMROLL = 'filmroll',
  SHOWCASE_ONELAB = 'showcaseOnelab',
  ANALOG_ALBUNS = 'analogAlbuns',

  // DREAMBOOKS STATES
  HOME_DREAMBOOKSPRO = 'dreambooksHome',
  ALBUNS = 'albuns',
  BOXES = 'boxes',
  PARENTS = 'parents',
  PROOFS = 'proofs',
  DVDS = 'dvds',
  GUEST = 'guest',
  STUDIO_BOX = 'studioBox',
  BOOKBOX = 'bookBox',
  SIGNATURES = 'signatures',
  SHOWCASE = 'showcase',
  SHOWCASE_COVERS = 'showcaseCovers',

  // LFM STATES
  HOME_LFMPRO = 'lfmHome',
  LFMPRODUCTS = 'lfmProducts',

  EDITOR = 'editor',
  UPLOADS = 'uploads',
  BAD_FLASH_PLAYER = 'badFlashPlayer',
  REJECTED_GDPR = 'rejectedGDPR'
}

// ServerFile base interface - from remote/common/ServerFile.as
export interface ServerFile {
  id: number;
  serverId: number;
  serverName: string;
  pathStructureId: number;
  md5?: string;
  localPath?: string;
  // Backend-provided absolute URLs. When present, PathService prefers these
  // over reconstructing the URL from serverId/serverName/pathStructure.
  url?: string;
  thumbnailUrl?: string;
  thumbUrl?: string;
}

// PathStructureInfo - from remote/common/PathStructureInfo.as
export interface PathStructureInfo {
  id: number;
  match: string;
  replace: string;
  replaceFolder?: string;
  type?: number;
}

// ServerInfo - from remote/common/ServerInfo.as
// Extended to support both legacy AMF fields and new Symfony API fields
export interface ServerInfo {
  id: number;
  url?: string;         // Legacy AMF field
  cms?: number;         // Legacy AMF field
  ssl?: number;         // Legacy AMF field
  // Symfony API fields (server:read group)
  server?: string;      // Server hostname from DB 'server' column (e.g. 'nbg1.your-objectstorage.com')
  serverUrl?: string;   // Public-facing URL from DB 'server_url' column (may be null)
  name?: string;        // Server name
  enable?: number;      // Enabled flag
}

export interface ProjectInfo extends ServerFile {
  name: string;
  project?: any;
  projectBytes?: ArrayBuffer;
  createdAt?: Date;
  updatedAt?: Date;
  thumbnail?: string;
  date?: Date;
  // Legacy fields from ProjectInfo.as
  editorTypeId?: number;
  productId?: number;
  productTypeId?: number;
  productName?: string;
  lastModifiedDateString?: string;
  creationDateString?: string;
  code?: string;
  ordered?: number;
  enabled?: boolean;
  uid?: string;
  saved?: boolean;
  deletedPhotos?: number;
  deleted?: number;
  orderItemId?: number;
  drafts?: any[];
}

export interface ProjectModel {
  product?: ProductInfo;
  info?: ProjectInfo;
  forceImport: boolean;
  lockContent: boolean;
  photosToRequest?: any[];
  fonts?: any[];
  photosToReplace?: any[];
  themes?: any[];
  theme?: any;
  albumFinishes?: any[];
  albumNumPages?: number;
  productUpdated: boolean;
}

export interface CartItem {
  id: number;
  product: ProductInfo;
  quantity: number;
  price: number;
  options?: any;
  orderName?: string;
  projectThumbnail?: string;
  /** Multi-design standalone drafts: one thumbnail per design, in order (first === projectThumbnail). */
  projectThumbnails?: string[];
  draftId?: number;
  draftServerName?: string;
}

export interface CartModel {
  items: CartItem[];
  totalPrice: number;
  totalItems: number;
}

// Note: Full OrderInfo for AMF operations is in order.model.ts
// This interface is for displaying order lists and basic order info
export interface OrderListItem {
  id: number;
  orderId?: number;  // Legacy field
  ref: string;
  status?: number;
  statusName?: string;
  date?: Date | string;
  orderDate?: string;  // Legacy field (format: "YYYY-MM-DD HH:MM:SS")
  createdAt?: Date;
  updatedAt?: Date;
  items?: CartItem[];
  groups?: any[];  // Order item groups
  total?: number;
  totalPrice?: number;
  shippingAddress?: any;
  billingAddress?: any;
  paymentMethod?: string;
  payment?: any;
  shipmentAddress?: any;
  shipmentMethod?: any;
  shipmentType?: any;
  trackingCode?: string;
  code?: string;
}

export interface ShareInfo {
  id: number;
  project: ProjectInfo;
  sharedWith?: string[];
  permissions?: number;
  createdAt: Date;
  expiresAt?: Date;
  url?: string;
  name?: string;
  thumbnail?: string;
}

export interface UploadInfo {
  id: number;
  fileName: string;
  fileSize: number;
  progress: number;
  status: UploadStatus;
  error?: string;
}

export enum UploadStatus {
  PENDING = 'pending',
  UPLOADING = 'uploading',
  COMPLETED = 'completed',
  ERROR = 'error',
  CANCELLED = 'cancelled'
}

export interface CurrencyInfo {
  id: number;
  code: string;
  symbol: string;
  name?: string;
  rate?: number;
  fractionalDigits: number;
  decimalSeparator: string;
  groupingSeparator: string;
  useGrouping: number;
}

export interface ServerInfo {
  version: string;
  maintenance: boolean;
  message?: string;
}
